import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  Address,
  Associate,
  ServiceAppointment as Appointment,
  AppointmentCase,
  QuoteDetail,
} from '@quorum/models/xs-resource';
import { ServiceAppointmentQueryParameters as AppointmentQueryParameters } from '@quorum/models/xs-query';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { DeepCopyPipe } from '@quorum/common-pipes';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { AcceptQuoteLineDialogComponent } from '../accept-quote-line-dialog/accept-quote-line-dialog.component';
// eslint-disable-next-line max-len
import { AppointmentCaseConfirmDeleteDialogComponent } from '../appointment-case-confirm-delete-dialog/appointment-case-confirm-delete-dialog.component';

@Component({
  selector: 'svc-appointment-freeform-vehicle',
  templateUrl: './appointment-freeform-vehicle.component.html',
  styleUrls: ['./appointment-freeform-vehicle.component.scss'],
})
export class AppointmentFreeformVehicleComponent implements OnInit, OnDestroy {
  appointmentId: number;
  vehicleId: number;
  caseListView = 'list';
  caseToEdit: AppointmentCase = null;
  actionSub: Subscription;
  updateSub: Subscription;
  createSub: Subscription;
  updateAssociateSub: Subscription;
  updateAddressSub: Subscription;
  updateSelectedAppointmentSub: Subscription;
  deleteSelectedAppointmentCaseSub: Subscription;
  updateQuoteLineSub: Subscription;
  serviceAppointmentDropdowns$: Observable<any>;
  appointmentForm: FormGroup;
  appointmentFormValid: boolean = false;
  constructor(
    private activatedRoute: ActivatedRoute,
    public serviceLaneFacade: ServiceLaneFacade,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    public dialog: MatDialog,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService
  ) {}

  ngOnInit() {
    this.serviceLaneFacade.selectedAppointment$.subscribe((g) => console.log(g));
    this.actionSub = this.serviceLaneFacade.setSelectedAppointmentCases$.subscribe();
    this.updateSub = this.serviceLaneFacade.updateSelectedAppointmentCase$.subscribe();
    this.createSub = this.serviceLaneFacade.createAppointmentCase$.subscribe();
    this.updateSelectedAppointmentSub = this.serviceLaneFacade.updateSelectedAppointment$.subscribe();
    this.deleteSelectedAppointmentCaseSub = this.serviceLaneFacade.deleteSelectedAppointmentCase$.subscribe();
    this.updateAssociateSub = this.serviceLaneFacade.updateAssociate$.subscribe();
    this.updateAddressSub = this.serviceLaneFacade.updateAddress$.subscribe();
    this.updateQuoteLineSub = this.serviceLaneFacade.updateQuoteLine$.subscribe();
    this.appointmentId = this.activatedRoute.snapshot.params.appointmentId;
    this.vehicleId = this.activatedRoute.snapshot.params.vehicleId;

    this.serviceLaneFacade.quoteLines$.subscribe((quoteLines) => console.log(quoteLines));
    this.serviceAppointmentDropdowns$ = combineLatest(
      this.serviceLaneDropdownFacade.serviceAppointmentDropdowns$,
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        take(1),
        switchMap((employee) => {
          return this.systemControlStateService
            .getSystemControlValues(['CL_GM_CADILLAC_SERVICE_CENTER'], employee.rooftopId)
            .pipe(startWith(null));
        })
      )
    ).pipe(
      map(([dropdowns, sysControl]) => {
        let dropdownsCopy = new DeepCopyPipe().transform(dropdowns);
        return dropdownsCopy;
      })
    );
  }

  displayEditCase(caseToEdit: AppointmentCase) {
    this.caseToEdit = caseToEdit;
    this.caseListView = 'edit';
  }

  caseValid(caseStatus: boolean) {}

  selectQuoteLine(quoteLine: QuoteDetail) {
    const dialogRef = this.dialog.open(AcceptQuoteLineDialogComponent, {
      width: '400px',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == 'yes') {
        let newCase: AppointmentCase = new AppointmentCase();
        newCase.appointmentId = this.appointmentId;
        newCase.employeeId = '13';
        newCase.opCode = quoteLine.opCode;
        newCase.quoteCaseNumber = quoteLine.sequence;
        newCase.recommendedHours = quoteLine.recommendedHours ? quoteLine.recommendedHours : 0;
        newCase.estimatedTime = quoteLine.recommendedHours ? quoteLine.recommendedHours : 0;
        newCase.warrantySubmissionTypeId = quoteLine.warrantySubmissionTypeId;

        newCase.requestDescription = quoteLine.serviceName;
        newCase.serviceGroupId = quoteLine.serviceGroupId;
        newCase.caseFromQuote = true;
        newCase.technicianId = quoteLine.technicianId;
        newCase.technicianGroupId = quoteLine.technicianGroupId;
        newCase.technicianGroupName = quoteLine.technicianGroupName;

        this.serviceLaneFacade.createAppointmentCaseOnServer(newCase, quoteLine);

        let updatedQuoteLine: QuoteDetail = new DeepCopyPipe().transform(quoteLine);
        updatedQuoteLine.sourceType = 'SA';
        updatedQuoteLine.sourceReferenceId = this.appointmentId;
        this.serviceLaneFacade.updateQuoteLineOnServer(updatedQuoteLine);
      }
    });
  }

  caseSaved(appointmentCase: AppointmentCase) {
    this.caseToEdit = null;
    this.caseListView = 'list';
    if (appointmentCase) {
      this.serviceLaneFacade.updateSelectedAppointmentCaseOnServer(appointmentCase);
    }
  }

  caseCreated(appointmentCase: AppointmentCase) {
    this.caseListView = 'list';
    if (appointmentCase != null) {
      this.serviceLaneFacade.createAppointmentCaseOnServer(appointmentCase, null);
    }
  }

  updateAppointment(appointment: Appointment, appointmentQueryParams: AppointmentQueryParameters) {
    this.serviceLaneFacade.updateAppointmentOnServer(appointment, appointmentQueryParams);
  }

  updateAssociate(associate: Associate) {
    this.serviceLaneFacade.updateAssociateOnServer(associate);
  }

  updateAddress(address: Address) {
    this.serviceLaneFacade.updateAddressOnServer(address);
  }

  appointmentFormEmit(appointmentForm: FormGroup) {
    this.appointmentForm = appointmentForm;
    this.appointmentFormValid = appointmentForm.valid;
  }

  ngOnDestroy() {
    this.actionSub.unsubscribe();
    this.updateSub.unsubscribe();
    this.createSub.unsubscribe();
    this.updateSelectedAppointmentSub.unsubscribe();
    this.deleteSelectedAppointmentCaseSub.unsubscribe();
    this.updateAssociateSub.unsubscribe();
    this.updateAddressSub.unsubscribe();
    this.updateQuoteLineSub.unsubscribe();
  }
}
